import { useEffect, useRef, useState } from 'react';
import { useClockifyStore } from '../../store/useClockifyStore';
import { formatDuration, formatDate } from '../../utils/timeUtils';
import { DeleteOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';

function toInputValue(timestamp) {
    const d = new Date(timestamp);
    const pad = (n) => String(n).padStart(2, '0');
    return (
        d.getFullYear() +
        '-' + pad(d.getMonth() + 1) +
        '-' + pad(d.getDate()) +
        'T' + pad(d.getHours()) +
        ':' + pad(d.getMinutes())
    );
}

function fromInputValue(value) {
    return new Date(value).getTime();
}

function getDateKey(timestamp) {
    const d = new Date(timestamp);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDateKey(dateKey) {
    const [year, month, day] = dateKey.split('-');
    const date = new Date(year, month - 1, day);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayKey = getDateKey(today.getTime());
    const yesterdayKey = getDateKey(yesterday.getTime());

    if (dateKey === todayKey) return '📅 Today';
    if (dateKey === yesterdayKey) return '📅 Yesterday';

    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return '📅 ' + date.toLocaleDateString('en-US', options);
}

function EditRow({ entry, onSave, onCancel, allowTaskEdit = false }) {
    const [taskVal, setTaskVal] = useState(entry.task);
    const [startVal, setStartVal] = useState(toInputValue(entry.startTime));
    const [endVal, setEndVal] = useState(toInputValue(entry.endTime || Date.now()));
    const [error, setError] = useState('');

    const handleSave = () => {
        const newStart = fromInputValue(startVal);
        const newEnd = fromInputValue(endVal);

        if (isNaN(newStart) || isNaN(newEnd)) {
            setError('Invalid date values.');
            return;
        }
        if (newEnd <= newStart) {
            setError('End time must be after start time.');
            return;
        }
        onSave(newStart, newEnd, taskVal);
    };

    return (
        <div style={editStyles.container}>
            {allowTaskEdit && (
                <div style={editStyles.field}>
                    <label style={editStyles.label}>📝 Task Name</label>
                    <input
                        type="text"
                        style={editStyles.input}
                        value={taskVal}
                        onChange={(e) => setTaskVal(e.target.value)}
                        maxLength={80}
                    />
                </div>
            )}

            <div style={editStyles.row}>
                <div style={editStyles.field}>
                    <label style={editStyles.label}>🟢 Start</label>
                    <input
                        type="datetime-local"
                        style={editStyles.input}
                        value={startVal}
                        onChange={(e) => { setStartVal(e.target.value); setError(''); }}
                    />
                </div>

                <div style={editStyles.field}>
                    <label style={editStyles.label}>🔴 End</label>
                    <input
                        type="datetime-local"
                        style={editStyles.input}
                        value={endVal}
                        onChange={(e) => { setEndVal(e.target.value); setError(''); }}
                    />
                </div>
            </div>

            {!error && startVal && endVal && (
                <div style={editStyles.preview}>
                    ⏱ New duration:{' '}
                    <strong style={{ color: '#c4b5fd' }}>
                        {formatDuration(Math.max(0, fromInputValue(endVal) - fromInputValue(startVal)))}
                    </strong>
                </div>
            )}

            {error && <div style={editStyles.error}>⚠ {error}</div>}

            <div style={editStyles.actions}>
                <button style={editStyles.saveBtn} onClick={handleSave}>✔ Save</button>
                <button style={editStyles.cancelBtn} onClick={onCancel}>✕ Cancel</button>
            </div>
        </div>
    );
}

export default function Clockify() {
    const {
        entries, activeEntry, isRunning,
        currentTask, setCurrentTask,
        startTimer, stopTimer,
        updateEntry, deleteEntry, clearEntries,
        projects, selectedProject, setSelectedProject,
        addProject, removeProject,
    } = useClockifyStore();

    const [elapsed, setElapsed] = useState(0);
    const [editingId, setEditingId] = useState(null);
    const [editingReportKey, setEditingReportKey] = useState(null);
    const [projectModalVisible, setProjectModalVisible] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectColor, setNewProjectColor] = useState('#03a9f4');
    const inputRef = useRef(null);

    useEffect(() => {
        document.title = 'Clockify';

        const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/svg+xml';
        link.rel = 'icon';
        link.href = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="%2303a9f4"/><circle cx="50" cy="50" r="35" fill="white"/><line x1="50" y1="50" x2="50" y2="25" stroke="%2303a9f4" stroke-width="3" stroke-linecap="round"/><line x1="50" y1="50" x2="65" y2="50" stroke="%2303a9f4" stroke-width="2" stroke-linecap="round"/><circle cx="50" cy="50" r="3" fill="%2303a9f4"/></svg>`;
        document.head.appendChild(link);
    }, []);

    useEffect(() => {
        if (!isRunning || !activeEntry) {
            document.title = 'Clockify'; // or whatever your default title is
            return;
        }

        const update = () => {
            const now = Date.now() - activeEntry.startTime;
            setElapsed(now);
            document.title = `${formatDuration(now)} - ${activeEntry.task}`;
        };

        update(); // immediate, no 1s delay
        const interval = setInterval(update, 1000);

        return () => {
            clearInterval(interval);
            document.title = 'Clockify';
        };
    }, [isRunning, activeEntry]);


    const handleStart = () => { startTimer(); inputRef.current?.blur(); };
    const handleKeyDown = (e) => { if (e.key === 'Enter' && !isRunning) handleStart(); };

    const handleSaveEdit = (id, newStart, newEnd, newTask) => {
        updateEntry(id, newStart, newEnd, newTask || entries.find(e => e.id === id)?.task);

        // If editing the active running task, force elapsed recalculation
        if (isRunning && activeEntry?.id === id) {
            setElapsed(Date.now() - newStart);
        }

        setEditingId(null);
    };

    const handleAddProject = () => {
        if (newProjectName.trim()) {
            addProject(newProjectName.trim(), newProjectColor);
            setNewProjectName('');
            setNewProjectColor('#03a9f4');
            setProjectModalVisible(false);
        }
    };

    const getProjectById = (id) => projects?.find(p => p.id === id) || { id: 'default', name: 'No Project', color: '#999999' };

    const processedReport = () => {
        const byDate = {};
        entries.forEach(entry => {
            const dateKey = getDateKey(entry.startTime);
            if (!byDate[dateKey]) byDate[dateKey] = [];
            byDate[dateKey].push(entry);
        });

        const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

        const report = sortedDates.map(dateKey => {
            const dayEntries = byDate[dateKey];
            const taskMap = {};

            dayEntries.forEach(entry => {
                // Group by task name AND project ID
                const taskKey = `${entry.task}|${entry.projectId || 'default'}`;
                if (!taskMap[taskKey]) {
                    taskMap[taskKey] = {
                        task: entry.task,
                        projectId: entry.projectId,  // Add this
                        totalDuration: 0,
                        count: 0,
                        entries: [],
                    };
                }
                taskMap[taskKey].totalDuration += entry.duration || 0;
                taskMap[taskKey].count += 1;
                taskMap[taskKey].entries.push(entry);
            });

            return {
                date: dateKey,
                tasks: Object.values(taskMap),
            };
        });

        return report;
    };


    const report = processedReport();

    const handleReportEdit = (dateKey, taskName) => {
        const key = `${dateKey}|${taskName}`;
        setEditingReportKey(editingReportKey === key ? null : key);
    };

    const handleReportDelete = (dateKey, taskName) => {
        const dayReport = report.find(r => r.date === dateKey);
        if (!dayReport) return;
        const taskData = dayReport.tasks.find(t => t.task === taskName);
        if (!taskData) return;

        taskData.entries.forEach(entry => deleteEntry(entry.id));
        setEditingReportKey(null);
    };

    return (
        <div style={styles.wrapper}>

            <div style={styles.card}>
                <div style={styles.timerDisplay}>
                    <span style={styles.timerText}>
                        {isRunning ? formatDuration(elapsed) : '00:00:00'}
                    </span>
                    {isRunning && <span style={styles.liveBadge}>● LIVE</span>}
                </div>

                <div style={styles.inputRow}>
                    <div style={styles.inputWrapper}>
                        <span style={styles.inputIcon}>📝</span>
                        <input
                            ref={inputRef}
                            style={{ ...styles.taskInput, ...(isRunning ? styles.taskInputDisabled : {}) }}
                            type="text"
                            placeholder="What are you working on?"
                            value={isRunning ? activeEntry?.task ?? '' : currentTask}
                            onChange={(e) => !isRunning && setCurrentTask(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isRunning}
                            maxLength={80}
                        />
                        {!isRunning && currentTask.length > 0 && (
                            <button style={styles.clearInputBtn} onClick={() => setCurrentTask('')} title="Clear">
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {projects && projects.length > 0 && (
                    <div style={styles.projectRow}>
                        <select
                            style={styles.projectSelect}
                            value={selectedProject || 'default'}
                            onChange={(e) => setSelectedProject(e.target.value)}
                            disabled={isRunning}
                        >
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                        <button
                            style={styles.addProjectBtn}
                            onClick={() => setProjectModalVisible(true)}
                            disabled={isRunning}
                        >
                            ➕ Project
                        </button>
                    </div>
                )}

                {projects && projects.filter(p => p.id !== 'default').length > 0 && (
                    <div style={styles.projectTags}>
                        {projects.filter(p => p.id !== 'default').map(p => (
                            <span key={p.id} style={{ ...styles.projectTag, borderColor: p.color }}>
                                <span style={{ color: p.color }}>●</span> {p.name}
                                <button
                                    style={styles.projectTagClose}
                                    onClick={() => removeProject(p.id)}
                                    title="Remove project"
                                >
                                    ✕
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {isRunning && activeEntry && (
                    <div style={styles.activeTaskBadge}>
                        <span style={styles.activeTaskLabel}>Tracking:</span>
                        <span style={styles.activeTaskName}>{activeEntry.task}</span>
                        {activeEntry.projectId && activeEntry.projectId !== 'default' && (
                            <span style={{ ...styles.activeProjectBadge, color: getProjectById(activeEntry.projectId).color }}>
                                ● {getProjectById(activeEntry.projectId).name}
                            </span>
                        )}
                        <button
                            style={styles.editActiveBtn}
                            onClick={() => setEditingId(activeEntry.id)}
                            title="Edit running task"
                        >
                            ✏️
                        </button>
                    </div>
                )}

                {editingId === activeEntry?.id && (
                    <EditRow
                        entry={{ ...activeEntry, endTime: Date.now() }}
                        onSave={(s, e, t) => handleSaveEdit(activeEntry.id, s, e, t)}
                        onCancel={() => setEditingId(null)}
                        allowTaskEdit={true}
                    />
                )}

                <div style={styles.buttonRow}>
                    <button
                        style={{ ...styles.btn, ...styles.startBtn, opacity: isRunning ? 0.4 : 1, cursor: isRunning ? 'not-allowed' : 'pointer' }}
                        onClick={handleStart} disabled={isRunning}
                    >▶ Start</button>

                    <button
                        style={{ ...styles.btn, ...styles.stopBtn, opacity: !isRunning ? 0.4 : 1, cursor: !isRunning ? 'not-allowed' : 'pointer' }}
                        onClick={stopTimer} disabled={!isRunning}
                    >■ Stop</button>
                </div>

                {isRunning && activeEntry && (
                    <p style={styles.startedAt}>Started at: {formatDate(activeEntry.startTime)}</p>
                )}
                {!isRunning && (
                    <p style={styles.hint}>
                        Press <kbd style={styles.kbd}>Enter</kbd> or click Start to begin
                    </p>
                )}
            </div>

            {projectModalVisible && (
                <div style={styles.modalOverlay} onClick={() => setProjectModalVisible(false)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Add New Project</h3>
                        <div style={styles.modalField}>
                            <label style={styles.modalLabel}>Project Name</label>
                            <input
                                type="text"
                                style={styles.modalInput}
                                value={newProjectName}
                                onChange={(e) => setNewProjectName(e.target.value)}
                                placeholder="Enter project name"
                                maxLength={50}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddProject()}
                            />
                        </div>
                        <div style={styles.modalField}>
                            <label style={styles.modalLabel}>Color</label>
                            <input
                                type="color"
                                style={styles.colorInput}
                                value={newProjectColor}
                                onChange={(e) => setNewProjectColor(e.target.value)}
                            />
                        </div>
                        <div style={styles.modalActions}>
                            <button style={styles.modalSaveBtn} onClick={handleAddProject}>Add Project</button>
                            <button style={styles.modalCancelBtn} onClick={() => setProjectModalVisible(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {entries.length > 0 && (
                <div style={styles.card2}>
                    <div style={styles.historyHeader}>
                        <h2 style={styles.subtitle}>📊 Report</h2>
                        <div style={styles.historyMeta}>
                            <span style={styles.entryCount}>{entries.length} entries</span>
                        </div>
                    </div>

                    {report.map(({ date, tasks }) => {
                        const dayTotal = tasks.reduce((sum, task) => sum + task.totalDuration, 0);
                        const dayCount = tasks.reduce((sum, task) => sum + task.count, 0);

                        return (
                            <div key={date} style={reportStyles.daySection}>
                                <div style={reportStyles.dayHeader}>
                                    <span>{formatDateKey(date)}</span>
                                    <span style={reportStyles.dayTotal}>
                                        {formatDuration(dayTotal)} <span style={reportStyles.dayCount}>({dayCount} {dayCount === 1 ? 'entry' : 'entries'})</span>
                                    </span>
                                </div>
                                <div style={reportStyles.taskList}>
                                    {tasks.map((task, idx) => {
                                        const reportKey = `${date}|${task.task}`;
                                        const isEditing = editingReportKey === reportKey;

                                        return (
                                            <div key={idx}>
                                                <div style={reportStyles.taskRow}>
                                                    <div style={reportStyles.taskName}>
                                                        {task.task}
                                                        {task.projectId && task.projectId !== 'default' && (
                                                            <span style={{ color: getProjectById(task.projectId).color, marginLeft: '0.5rem' }}>
                                                                ● {getProjectById(task.projectId).name}
                                                            </span>
                                                        )}
                                                        {task.count > 1 && (
                                                            <span style={reportStyles.countBadge}>×{task.count}</span>
                                                        )}
                                                    </div>

                                                    <div style={reportStyles.taskDuration}>
                                                        {formatDuration(task.totalDuration)}
                                                    </div>
                                                    <div style={reportStyles.taskActions}>
                                                        <button
                                                            style={reportStyles.startBtn}
                                                            onClick={() => {
                                                                setCurrentTask(task.task);
                                                                startTimer();
                                                            }}
                                                            title="Start this task"
                                                            disabled={isRunning}
                                                        >
                                                            ▶
                                                        </button>
                                                        <button
                                                            style={{
                                                                ...reportStyles.editBtn,
                                                                ...(isEditing ? reportStyles.editBtnActive : {}),
                                                            }}
                                                            onClick={() => handleReportEdit(date, task.task)}
                                                            title="Edit times"
                                                        >
                                                            {isEditing ? <CloseOutlined /> : <EditOutlined />}
                                                        </button>
                                                        <button
                                                            style={reportStyles.deleteBtn}
                                                            onClick={() => handleReportDelete(date, task.task)}
                                                            title="Delete all entries for this task"
                                                        >
                                                            <DeleteOutlined />
                                                        </button>
                                                    </div>
                                                </div>

                                                {isEditing && (
                                                    <div style={reportStyles.entriesExpanded}>
                                                        {task.entries.map((entry) => (
                                                            <div key={entry.id} style={reportStyles.entryItem}>
                                                                {editingId === entry.id ? (
                                                                    <EditRow
                                                                        entry={entry}
                                                                        onSave={(s, e, t) => handleSaveEdit(entry.id, s, e, t)}
                                                                        onCancel={() => setEditingId(null)}
                                                                        allowTaskEdit={true}
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        <div style={reportStyles.entryTime}>
                                                                            {formatDate(entry.startTime)} → {formatDate(entry.endTime)}
                                                                        </div>
                                                                        <div style={reportStyles.entryDuration}>
                                                                            {formatDuration(entry.duration)}
                                                                        </div>
                                                                        {entry.projectId && entry.projectId !== 'default' && (
                                                                            <div style={{ ...reportStyles.entryProject, color: getProjectById(entry.projectId).color }}>
                                                                                ● {getProjectById(entry.projectId).name}
                                                                            </div>
                                                                        )}
                                                                        <div style={reportStyles.entryActions}>
                                                                            <button
                                                                                style={reportStyles.entryEditBtn}
                                                                                onClick={() => setEditingId(entry.id)}
                                                                                title="Edit this entry"
                                                                            >
                                                                                <EditOutlined />
                                                                            </button>
                                                                            <button
                                                                                style={reportStyles.entryDeleteBtn}
                                                                                onClick={() => deleteEntry(entry.id)}
                                                                                title="Delete this entry"
                                                                            >
                                                                                <DeleteOutlined />
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    <button style={styles.clearBtn} onClick={clearEntries}>
                        🗑 Clear All Entries
                    </button>
                </div>
            )}
        </div>
    );
}

const styles = {
    wrapper: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)',
        padding: '2rem',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        display: 'flex',
        flexDirection: 'row-reverse',
        gap: '2rem'
    },
    card: {
        maxWidth: '700px',
        width: '35%',
        margin: '0 auto 2rem',
        background: 'rgba(30, 30, 46, 0.95)',
        borderRadius: '20px',
        padding: '2.5rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        inset: '0',
        top: '32px',
        height: 'fit-content'
    },
    card2: {
        width: '65%',
        margin: '0 auto 2rem',
        background: 'rgba(30, 30, 46, 0.95)',
        borderRadius: '20px',
        padding: '2.5rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
    },
    title: {
        fontSize: '2.5rem',
        fontWeight: '700',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #a78bfa 0%, #c4b5fd 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '2rem',
        letterSpacing: '-0.02em',
    },
    timerDisplay: {
        textAlign: 'center',
        marginBottom: '2rem',
        position: 'relative',
    },
    timerText: {
        fontSize: '4rem',
        fontWeight: '700',
        color: '#c4b5fd',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '0.05em',
        textShadow: '0 0 30px rgba(167, 139, 250, 0.3)',
    },
    liveBadge: {
        position: 'absolute',
        top: '0',
        right: '50%',
        transform: 'translateX(200px)',
        background: 'rgba(239, 68, 68, 0.2)',
        color: '#ef4444',
        padding: '0.4rem 0.8rem',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        animation: 'pulse 2s ease-in-out infinite',
    },
    inputRow: {
        marginBottom: '1.5rem',
    },
    inputWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    inputIcon: {
        position: 'absolute',
        left: '1rem',
        fontSize: '1.2rem',
        pointerEvents: 'none',
        zIndex: 1,
    },
    taskInput: {
        width: '100%',
        padding: '1rem 3rem 1rem 3rem',
        fontSize: '1.1rem',
        background: 'rgba(45, 45, 68, 0.6)',
        border: '2px solid rgba(167, 139, 250, 0.2)',
        borderRadius: '12px',
        color: '#e5e7eb',
        outline: 'none',
        transition: 'all 0.3s ease',
        fontFamily: 'inherit',
    },
    taskInputDisabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
    },
    clearInputBtn: {
        position: 'absolute',
        right: '1rem',
        background: 'rgba(239, 68, 68, 0.2)',
        border: 'none',
        color: '#ef4444',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        cursor: 'pointer',
        fontSize: '0.9rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
    },
    projectRow: {
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '1rem',
    },
    projectSelect: {
        flex: 1,
        padding: '0.75rem 1rem',
        fontSize: '0.95rem',
        background: 'rgba(45, 45, 68, 0.6)',
        border: '2px solid rgba(167, 139, 250, 0.2)',
        borderRadius: '10px',
        color: '#e5e7eb',
        outline: 'none',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
    },
    addProjectBtn: {
        padding: '0.75rem 1.5rem',
        fontSize: '0.9rem',
        background: 'rgba(167, 139, 250, 0.15)',
        border: '2px solid rgba(167, 139, 250, 0.3)',
        borderRadius: '10px',
        color: '#c4b5fd',
        cursor: 'pointer',
        fontWeight: '600',
        transition: 'all 0.3s ease',
    },
    projectTags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginBottom: '1rem',
    },
    projectTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.4rem 0.8rem',
        background: 'rgba(45, 45, 68, 0.6)',
        border: '1px solid',
        borderRadius: '20px',
        fontSize: '0.85rem',
        color: '#e5e7eb',
    },
    projectTagClose: {
        background: 'none',
        border: 'none',
        color: '#ef4444',
        cursor: 'pointer',
        fontSize: '0.9rem',
        padding: '0 0.2rem',
        marginLeft: '0.2rem',
    },
    activeTaskBadge: {
        background: 'rgba(167, 139, 250, 0.1)',
        border: '1px solid rgba(167, 139, 250, 0.3)',
        borderRadius: '10px',
        padding: '0.75rem 1rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
    },
    activeTaskLabel: {
        fontSize: '0.85rem',
        color: '#9ca3af',
        fontWeight: '500',
    },
    activeTaskName: {
        fontSize: '0.95rem',
        color: '#c4b5fd',
        fontWeight: '600',
        flex: 1,
    },
    activeProjectBadge: {
        fontSize: '0.85rem',
        fontWeight: '500',
    },
    editActiveBtn: {
        background: 'rgba(167, 139, 250, 0.15)',
        border: '1px solid rgba(167, 139, 250, 0.3)',
        color: '#c4b5fd',
        padding: '0.3rem 0.6rem',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.85rem',
    },
    buttonRow: {
        display: 'flex',
        gap: '1rem',
        marginBottom: '1rem',
    },
    btn: {
        flex: 1,
        padding: '1rem',
        fontSize: '1.1rem',
        fontWeight: '600',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontFamily: 'inherit',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    },
    startBtn: {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white',
    },
    stopBtn: {
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
    },
    startedAt: {
        textAlign: 'center',
        fontSize: '0.9rem',
        color: '#9ca3af',
        marginTop: '1rem',
    },
    hint: {
        textAlign: 'center',
        fontSize: '0.9rem',
        color: '#6b7280',
        marginTop: '1rem',
    },
    kbd: {
        background: 'rgba(167, 139, 250, 0.15)',
        padding: '0.2rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.85rem',
        fontFamily: 'monospace',
        border: '1px solid rgba(167, 139, 250, 0.3)',
        color: '#c4b5fd',
    },
    historyHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid rgba(167, 139, 250, 0.2)',
    },
    subtitle: {
        fontSize: '1.8rem',
        fontWeight: '700',
        background: 'linear-gradient(135deg, #a78bfa 0%, #c4b5fd 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        margin: 0,
    },
    historyMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
    },
    entryCount: {
        fontSize: '0.9rem',
        color: '#9ca3af',
        background: 'rgba(167, 139, 250, 0.1)',
        padding: '0.4rem 0.8rem',
        borderRadius: '20px',
        fontWeight: '500',
    },
    clearBtn: {
        width: '100%',
        padding: '0.9rem',
        marginTop: '1.5rem',
        fontSize: '1rem',
        fontWeight: '600',
        background: 'rgba(239, 68, 68, 0.15)',
        border: '2px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '12px',
        color: '#ef4444',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontFamily: 'inherit',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)',
    },
    modal: {
        background: 'rgba(30, 30, 46, 0.98)',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '400px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        border: '1px solid rgba(167, 139, 250, 0.2)',
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#c4b5fd',
        marginBottom: '1.5rem',
        textAlign: 'center',
    },
    modalField: {
        marginBottom: '1.5rem',
    },
    modalLabel: {
        display: 'block',
        fontSize: '0.9rem',
        color: '#9ca3af',
        marginBottom: '0.5rem',
        fontWeight: '500',
    },
    modalInput: {
        width: '100%',
        padding: '0.75rem 1rem',
        fontSize: '1rem',
        background: 'rgba(45, 45, 68, 0.6)',
        border: '2px solid rgba(167, 139, 250, 0.2)',
        borderRadius: '10px',
        color: '#e5e7eb',
        outline: 'none',
        fontFamily: 'inherit',
    },
    colorInput: {
        width: '100%',
        height: '50px',
        padding: '0.25rem',
        background: 'rgba(45, 45, 68, 0.6)',
        border: '2px solid rgba(167, 139, 250, 0.2)',
        borderRadius: '10px',
        cursor: 'pointer',
    },
    modalActions: {
        display: 'flex',
        gap: '0.75rem',
        marginTop: '2rem',
    },
    modalSaveBtn: {
        flex: 1,
        padding: '0.75rem',
        fontSize: '1rem',
        fontWeight: '600',
        background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
        border: 'none',
        borderRadius: '10px',
        color: 'white',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    modalCancelBtn: {
        flex: 1,
        padding: '0.75rem',
        fontSize: '1rem',
        fontWeight: '600',
        background: 'rgba(239, 68, 68, 0.15)',
        border: '2px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '10px',
        color: '#ef4444',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
};

const reportStyles = {
    daySection: {
        marginBottom: '2rem',
    },
    dayHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        background: 'rgba(167, 139, 250, 0.1)',
        borderRadius: '10px',
        marginBottom: '0.75rem',
        fontSize: '1.1rem',
        fontWeight: '600',
        color: '#c4b5fd',
    },
    dayTotal: {
        fontSize: '1rem',
        color: '#a78bfa',
    },
    dayCount: {
        fontSize: '0.85rem',
        color: '#9ca3af',
        fontWeight: '400',
    },
    taskList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    taskRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.75rem 1rem',
        background: 'rgba(45, 45, 68, 0.4)',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(167, 139, 250, 0.1)',
    },
    taskName: {
        flex: 1,
        fontSize: '0.95rem',
        color: '#e5e7eb',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    countBadge: {
        fontSize: '0.75rem',
        background: 'rgba(167, 139, 250, 0.2)',
        color: '#c4b5fd',
        padding: '0.2rem 0.5rem',
        borderRadius: '10px',
        fontWeight: '600',
    },
    taskDuration: {
        fontSize: '0.9rem',
        color: '#a78bfa',
        fontWeight: '600',
        marginRight: '1rem',
        fontVariantNumeric: 'tabular-nums',
    },
    taskActions: {
        display: 'flex',
        gap: '0.5rem',
    },
    startBtn: {
        background: 'rgba(16, 185, 129, 0.15)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        color: '#10b981',
        padding: '0.4rem 0.7rem',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        transition: 'all 0.2s ease',
    },
    editBtn: {
        background: 'rgba(59, 130, 246, 0.15)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        color: '#3b82f6',
        padding: '0.4rem 0.7rem',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        transition: 'all 0.2s ease',
    },
    editBtnActive: {
        background: 'rgba(59, 130, 246, 0.3)',
        borderColor: 'rgba(59, 130, 246, 0.5)',
    },
    deleteBtn: {
        background: 'rgba(239, 68, 68, 0.15)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        color: '#ef4444',
        padding: '0.4rem 0.7rem',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        transition: 'all 0.2s ease',
    },
    entriesExpanded: {
        marginTop: '0.5rem',
        marginLeft: '1rem',
        paddingLeft: '1rem',
        borderLeft: '2px solid rgba(167, 139, 250, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    entryItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.6rem 0.8rem',
        background: 'rgba(45, 45, 68, 0.3)',
        borderRadius: '6px',
        fontSize: '0.85rem',
        gap: '0.75rem',
    },
    entryTime: {
        flex: 1,
        color: '#9ca3af',
        fontSize: '0.8rem',
    },
    entryDuration: {
        color: '#a78bfa',
        fontWeight: '600',
        fontVariantNumeric: 'tabular-nums',
    },
    entryProject: {
        fontSize: '0.8rem',
        fontWeight: '500',
    },
    entryActions: {
        display: 'flex',
        gap: '0.4rem',
    },
    entryEditBtn: {
        background: 'rgba(59, 130, 246, 0.15)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        color: '#3b82f6',
        padding: '0.3rem 0.5rem',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.75rem',
    },
    entryDeleteBtn: {
        background: 'rgba(239, 68, 68, 0.15)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        color: '#ef4444',
        padding: '0.3rem 0.5rem',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.75rem',
    },
};

const editStyles = {
    container: {
        background: 'rgba(59, 130, 246, 0.1)',
        border: '2px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
    },
    field: {
        marginBottom: '1rem',
    },
    row: {
        display: 'flex',
        gap: '1rem',
    },
    label: {
        display: 'block',
        fontSize: '0.85rem',
        color: '#9ca3af',
        marginBottom: '0.5rem',
        fontWeight: '500',
    },
    input: {
        width: '100%',
        padding: '0.75rem',
        fontSize: '0.95rem',
        background: 'rgba(45, 45, 68, 0.6)',
        border: '2px solid rgba(167, 139, 250, 0.2)',
        borderRadius: '8px',
        color: '#e5e7eb',
        outline: 'none',
        fontFamily: 'inherit',
    },
    preview: {
        fontSize: '0.9rem',
        color: '#9ca3af',
        marginTop: '0.75rem',
        padding: '0.75rem',
        background: 'rgba(167, 139, 250, 0.1)',
        borderRadius: '8px',
        textAlign: 'center',
    },
    error: {
        fontSize: '0.9rem',
        color: '#ef4444',
        marginTop: '0.75rem',
        padding: '0.75rem',
        background: 'rgba(239, 68, 68, 0.1)',
        borderRadius: '8px',
        textAlign: 'center',
    },
    actions: {
        display: 'flex',
        gap: '0.75rem',
        marginTop: '1rem',
    },
    saveBtn: {
        flex: 1,
        padding: '0.75rem',
        fontSize: '0.95rem',
        fontWeight: '600',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        border: 'none',
        borderRadius: '8px',
        color: 'white',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    cancelBtn: {
        flex: 1,
        padding: '0.75rem',
        fontSize: '0.95rem',
        fontWeight: '600',
        background: 'rgba(239, 68, 68, 0.15)',
        border: '2px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '8px',
        color: '#ef4444',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
};

